import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { COUPONS, byId } from '../data/catalog';

/** Pozycja w koszyku. `sku` wskazuje konkretny wariant produktu. */
export interface CartLine {
  productId: string;
  sku: string;
  qty: number;
}

export interface Order {
  id: string;
  createdAt: string;
  lines: CartLine[];
  shippingMethod: string;
  shippingCost: number;
  paymentMethod: string;
  coupon?: string;
  discount: number;
  tax: number;
  total: number;
  email: string;
  /** Ile sztuk danego SKU zostało już zwróconych. */
  refunded: Record<string, number>;
}

const CART_KEY = 'nf_cart';
const COUPON_KEY = 'nf_coupon';
const ORDERS_KEY = 'nf_orders';
const USER_KEY = 'nf_user';

export interface User {
  id: string;
  email: string;
  tier: 'standard' | 'premium';
}

interface CartCtx {
  lines: CartLine[];
  coupon?: string;
  orders: Order[];
  user: User | null;
  add: (line: CartLine) => void;
  setQty: (sku: string, qty: number) => void;
  remove: (sku: string) => void;
  clear: () => void;
  applyCoupon: (code?: string) => boolean;
  login: (email: string) => User;
  logout: () => void;
  saveOrder: (o: Order) => void;
  markRefund: (orderId: string, sku: string, qty: number) => void;
  subtotal: number;
  discount: number;
  tax: number;
  count: number;
}

const Ctx = createContext<CartCtx | null>(null);

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* tryb prywatny */
  }
}

export function unitPrice(line: CartLine): number {
  const p = byId(line.productId);
  const v = p?.variants.find((x) => x.sku === line.sku);
  if (!p || !v) return 0;
  return p.price + v.priceDelta;
}

/** Standardowy obiekt item ze specyfikacji GA4 ecommerce — tylko udokumentowane pola. */
export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  item_category2?: string;
  item_category3?: string;
  item_variant: string;
  price: number;
  quantity: number;
  discount?: number;
}

/**
 * Mapuje pozycję koszyka na standardowy item GA4. Wspólne dla wszystkich
 * zdarzeń ecommerce operujących na CartLine (view_cart, remove_from_cart,
 * begin_checkout, purchase) — bez tego ta sama logika żyłaby osobno w
 * każdym z tych miejsc i rozjeżdżała się przy każdej poprawce.
 *
 * `null`, gdy SKU już nie istnieje w katalogu (np. produkt wycofany) —
 * dokładnie tak samo, jak reszta UI cicho pomija takie linie.
 */
export function cartLineToItem(line: CartLine): GA4Item | null {
  const p = byId(line.productId);
  const v = p?.variants.find((x) => x.sku === line.sku);
  if (!p || !v) return null;

  const price = Math.round(unitPrice(line) * 100) / 100;
  const discount = p.listPrice && p.listPrice > p.price ? Math.round((p.listPrice - p.price) * 100) / 100 : undefined;

  return {
    item_id: v.sku,
    item_name: p.name,
    item_brand: p.brand,
    item_category: p.category,
    ...(p.subcategory ? { item_category2: p.subcategory } : {}),
    ...(p.type ? { item_category3: p.type } : {}),
    item_variant: v.label,
    price,
    quantity: line.qty,
    ...(discount !== undefined ? { discount } : {}),
  };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => load<CartLine[]>(CART_KEY, []));
  const [coupon, setCoupon] = useState<string | undefined>(() => load<string | undefined>(COUPON_KEY, undefined) ?? undefined);
  const [orders, setOrders] = useState<Order[]>(() => load<Order[]>(ORDERS_KEY, []));
  const [user, setUser] = useState<User | null>(() => load<User | null>(USER_KEY, null));

  useEffect(() => save(CART_KEY, lines), [lines]);
  useEffect(() => save(COUPON_KEY, coupon ?? null), [coupon]);
  useEffect(() => save(ORDERS_KEY, orders), [orders]);
  useEffect(() => save(USER_KEY, user), [user]);

  // Synchronizacja User-ID do dataLayer — mechanizm osobny od zdarzenia
  // `login` (to w Account.tsx). GA4 czyta user_id z modelu dataLayer przy
  // KAŻDYM zdarzeniu, nie tylko w chwili logowania — dlatego ten efekt musi
  // odpalić się też przy starcie aplikacji, gdy użytkownik jest już
  // zalogowany z poprzedniej wizyty (odtworzony z localStorage), inaczej
  // pierwsze zdarzenia nowej sesji poszłyby bez identyfikatora. Wylogowanie
  // (user → null) jawnie czyści klucz wartością `undefined` — GTM usuwa
  // wtedy user_id z modelu, więc kolejna osoba na tej samej przeglądarce
  // nie odziedziczy cudzej tożsamości.
  //
  // Nigdy surowy e-mail — tylko wewnętrzne, niepersonalne `user.id`.
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_id: user?.id ?? undefined });
  }, [user]);

  const add = useCallback((line: CartLine) => {
    setLines((prev) => {
      const i = prev.findIndex((l) => l.sku === line.sku);
      if (i === -1) return [...prev, line];
      const next = [...prev];
      next[i] = { ...next[i], qty: next[i].qty + line.qty };
      return next;
    });
  }, []);

  const setQty = useCallback((sku: string, qty: number) => {
    setLines((prev) => prev.map((l) => (l.sku === sku ? { ...l, qty: Math.max(1, qty) } : l)));
  }, []);

  const remove = useCallback((sku: string) => setLines((prev) => prev.filter((l) => l.sku !== sku)), []);
  const clear = useCallback(() => setLines([]), []);

  const applyCoupon = useCallback((code?: string) => {
    if (code === undefined) {
      setCoupon(undefined);
      return true;
    }
    const c = code.trim().toUpperCase();
    if (!COUPONS[c]) return false;
    setCoupon(c);
    return true;
  }, []);

  const login = useCallback((email: string) => {
    const u: User = {
      id: 'u_' + Math.abs([...email].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) | 0, 7)).toString(36),
      email,
      tier: email.endsWith('.pl') ? 'premium' : 'standard',
    };
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => setUser(null), []);
  const saveOrder = useCallback((o: Order) => setOrders((prev) => [o, ...prev].slice(0, 25)), []);

  const markRefund = useCallback((orderId: string, sku: string, qty: number) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? { ...o, refunded: { ...o.refunded, [sku]: (o.refunded[sku] ?? 0) + qty } } : o,
      ),
    );
  }, []);

  const value = useMemo<CartCtx>(() => {
    const subtotal = Math.round(lines.reduce((s, l) => s + unitPrice(l) * l.qty, 0) * 100) / 100;
    const c = coupon ? COUPONS[coupon] : undefined;
    const discount = c ? Math.round(subtotal * c.rate * 100) / 100 : 0;
    const net = subtotal - discount;
    return {
      lines,
      coupon,
      orders,
      user,
      add,
      setQty,
      remove,
      clear,
      applyCoupon,
      login,
      logout,
      saveOrder,
      markRefund,
      subtotal,
      discount,
      tax: Math.round(net * 0.23 * 100) / 100,
      count: lines.reduce((s, l) => s + l.qty, 0),
    };
  }, [lines, coupon, orders, user, add, setQty, remove, clear, applyCoupon, login, logout, saveOrder, markRefund]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart(): CartCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCart musi być wewnątrz <CartProvider>');
  return ctx;
}
